import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Loader2, Search, TrendingUp, TrendingDown, Target, 
  AlertTriangle, RefreshCw, ArrowUpRight, Filter, Zap
} from "lucide-react";
import { toast } from "sonner";
import AnalysisSections from "../components/AnalysisSections";

import { API } from "../config/api";

const ScannerPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all, nasdaq, nyse, strong

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/market/high-rr-scanner`);
      setData(response.data);
      toast.success(`Found ${response.data.opportunities_found} high R:R opportunities`);
    } catch (error) {
      console.error("Error fetching scanner data:", error);
      toast.error("Failed to scan stocks. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredOpportunities = data?.opportunities?.filter(opp => {
    if (filter === "nasdaq") return opp.exchange === "NASDAQ";
    if (filter === "nyse") return opp.exchange === "NYSE";
    if (filter === "strong") return opp.signal_strength === "STRONG";
    return true;
  }) || [];

  const getChangeColor = (val) => {
    if (val > 0) return "text-green-600";
    if (val < 0) return "text-red-600";
    return "text-slate-600";
  };

  const getRRBadgeColor = (ratio) => {
    if (ratio >= 6) return "bg-green-600 text-white";
    if (ratio >= 5) return "bg-green-500 text-white";
    if (ratio >= 4.5) return "bg-yellow-500 text-white";
    return "bg-slate-400 text-white";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <a 
              href="/" 
              className="text-indigo-600 hover:text-indigo-800 text-sm font-medium flex items-center gap-1 mb-2" target="_blank" rel="noopener noreferrer">
              ← Back to Dashboard
            </a>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Target className="w-7 h-7 text-indigo-600" />
              High Risk/Reward Scanner
            </h1>
            <p className="text-slate-600 text-sm mt-1">
              Scanning NASDAQ & NYSE for 5:1+ Risk/Reward Opportunities
            </p>
          </div>
          <div className="flex items-center gap-3">
            <a 
              href="/" 
              className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2" target="_blank" rel="noopener noreferrer">
              <ArrowUpRight className="w-4 h-4" />
              Home
            </a>
            <Button 
              onClick={fetchData} 
              disabled={loading}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              {loading ? "Scanning..." : "Refresh Scan"}
            </Button>
          </div>
        </div>

        {/* Analysis Sections Navigation */}
        <AnalysisSections currentPage="scanner" />

        {/* Summary Cards */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="border-indigo-200">
              <CardContent className="p-4">
                <p className="text-xs text-slate-500">Stocks Scanned</p>
                <p className="text-2xl font-bold text-indigo-700">{data.total_scanned}</p>
              </CardContent>
            </Card>
            <Card className="border-green-200">
              <CardContent className="p-4">
                <p className="text-xs text-slate-500">Opportunities Found</p>
                <p className="text-2xl font-bold text-green-700">{data.opportunities_found}</p>
              </CardContent>
            </Card>
            <Card className="border-yellow-200">
              <CardContent className="p-4">
                <p className="text-xs text-slate-500">Avg R:R Ratio</p>
                <p className="text-2xl font-bold text-yellow-700">{data.summary?.avg_rr_ratio}:1</p>
              </CardContent>
            </Card>
            <Card className="border-purple-200">
              <CardContent className="p-4">
                <p className="text-xs text-slate-500">Strong Signals</p>
                <p className="text-2xl font-bold text-purple-700">{data.summary?.strong_signals}</p>
              </CardContent>
            </Card>
            <Card className="border-blue-200">
              <CardContent className="p-4">
                <p className="text-xs text-slate-500">Min Threshold</p>
                <p className="text-2xl font-bold text-blue-700">5:1</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-500" />
          <span className="text-sm text-slate-600">Filter:</span>
          {['all', 'nasdaq', 'nyse', 'strong'].map((f) => (
            <Button
              key={f}
              size="sm"
              variant={filter === f ? "default" : "outline"}
              onClick={() => setFilter(f)}
              className={filter === f ? "bg-indigo-600" : ""}
            >
              {f === 'all' ? 'All' : f === 'strong' ? 'Strong Only' : f.toUpperCase()}
            </Button>
          ))}
        </div>

        {/* Loading State */}
        {loading && !data && (
          <Card className="border-indigo-200">
            <CardContent className="p-4">
              <div className="animate-pulse space-y-3">
                <div className="flex items-center justify-between mb-4">
                  <div className="bg-gray-200 rounded h-5 w-48" />
                  <div className="bg-gray-200 rounded h-5 w-20" />
                </div>
                <div className="bg-gray-100 rounded px-4 py-2.5 flex gap-4">
                  {[1,2,3,4,5,6,7].map(i => <div key={i} className="bg-gray-200 rounded h-3 flex-1" />)}
                </div>
                {Array.from({length:10}).map((_,r) => (
                  <div key={r} className="px-4 py-2.5 flex gap-4 border-t border-gray-100">
                    {[1,2,3,4,5,6,7].map(c => <div key={c} className="bg-gray-200 rounded h-3 flex-1" />)}
                  </div>
                ))}
              </div>
              <p className="text-slate-600 font-medium text-center mt-4">Scanning NASDAQ & NYSE stocks...</p>
              <p className="text-slate-500 text-sm text-center">Analyzing support/resistance levels</p>
            </CardContent>
          </Card>
        )}

        {/* Opportunities Table */}
        {data && (
          <Card className="border-indigo-200">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Zap className="w-5 h-5 text-yellow-500" />
                5:1+ Risk/Reward Opportunities
                <Badge variant="outline" className="ml-2">
                  {filteredOpportunities.length} stocks
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {filteredOpportunities.length === 0 ? (
                <div className="text-center py-10 text-slate-500">
                  <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-yellow-500" />
                  <p>No opportunities found with current filters</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-100 text-slate-700">
                        <th className="text-left py-3 px-3 font-semibold">Ticker</th>
                        <th className="text-left py-3 px-3 font-semibold hidden md:table-cell">Company</th>
                        <th className="text-center py-3 px-3 font-semibold">Price</th>
                        <th className="text-center py-3 px-3 font-semibold">Change</th>
                        <th className="text-center py-3 px-3 font-semibold">R:R Ratio</th>
                        <th className="text-center py-3 px-3 font-semibold hidden lg:table-cell">Support</th>
                        <th className="text-center py-3 px-3 font-semibold hidden lg:table-cell">Resistance</th>
                        <th className="text-center py-3 px-3 font-semibold">Potential</th>
                        <th className="text-center py-3 px-3 font-semibold">Signal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOpportunities.map((opp, idx) => (
                        <tr 
                          key={opp.ticker}
                          className={`border-b border-slate-100 hover:bg-indigo-50 ${
                            idx < 3 ? 'bg-green-50' : ''
                          }`}
                        >
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-2">
                              <TrendingUp className="w-4 h-4 text-green-500" />
                              <a 
                                href={`/stock?ticker=${opp.ticker}`}
                                className="font-bold text-indigo-600 hover:text-indigo-800 hover:underline" target="_blank" rel="noopener noreferrer">
                                {opp.ticker}
                              </a>
                              <Badge variant="outline" className="text-[10px]">
                                {opp.exchange}
                              </Badge>
                            </div>
                          </td>
                          <td className="py-3 px-3 text-slate-600 hidden md:table-cell truncate max-w-[150px]">
                            {opp.company_name}
                          </td>
                          <td className="py-3 px-3 text-center font-semibold">
                            ${opp.current_price}
                          </td>
                          <td className={`py-3 px-3 text-center font-semibold ${getChangeColor(opp.change_pct)}`}>
                            {opp.change_pct > 0 ? '+' : ''}{opp.change_pct}%
                          </td>
                          <td className="py-3 px-3 text-center">
                            <Badge className={getRRBadgeColor(opp.risk_reward_ratio)}>
                              {opp.risk_reward_ratio}:1
                            </Badge>
                          </td>
                          <td className="py-3 px-3 text-center text-green-600 hidden lg:table-cell">
                            ${opp.support}
                          </td>
                          <td className="py-3 px-3 text-center text-red-600 hidden lg:table-cell">
                            ${opp.resistance}
                          </td>
                          <td className="py-3 px-3 text-center">
                            <span className="text-green-600 font-bold">+{opp.potential_gain_pct}%</span>
                            <span className="text-slate-400 mx-1">/</span>
                            <span className="text-red-600">-{opp.potential_loss_pct}%</span>
                          </td>
                          <td className="py-3 px-3 text-center">
                            <Badge className={opp.signal_strength === 'STRONG' ? 'bg-green-600' : 'bg-yellow-500'}>
                              {opp.signal_strength}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Trade Setup Card - for top opportunity */}
        {filteredOpportunities.length > 0 && (
          <Card className="border-2 border-green-400 bg-gradient-to-br from-green-50 to-emerald-50">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg text-green-800">
                <Target className="w-5 h-5" />
                Top Trade Setup: 
                <a 
                  href={`/stock?ticker=${filteredOpportunities[0].ticker}`}
                  className="text-indigo-600 hover:text-indigo-800 hover:underline" target="_blank" rel="noopener noreferrer">
                  {filteredOpportunities[0].ticker}
                </a>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white p-3 rounded-lg border border-green-200">
                  <p className="text-xs text-slate-500 mb-1">Entry Price</p>
                  <p className="text-xl font-bold text-slate-800">
                    ${filteredOpportunities[0].entry_price}
                  </p>
                </div>
                <div className="bg-white p-3 rounded-lg border border-red-200">
                  <p className="text-xs text-slate-500 mb-1">Stop Loss</p>
                  <p className="text-xl font-bold text-red-600">
                    ${filteredOpportunities[0].stop_loss}
                  </p>
                  <p className="text-xs text-red-500">
                    Risk: ${filteredOpportunities[0].risk}
                  </p>
                </div>
                <div className="bg-white p-3 rounded-lg border border-green-200">
                  <p className="text-xs text-slate-500 mb-1">Target Price</p>
                  <p className="text-xl font-bold text-green-600">
                    ${filteredOpportunities[0].target}
                  </p>
                  <p className="text-xs text-green-500">
                    Reward: ${filteredOpportunities[0].reward}
                  </p>
                </div>
                <div className="bg-white p-3 rounded-lg border border-indigo-200">
                  <p className="text-xs text-slate-500 mb-1">Risk/Reward</p>
                  <p className="text-xl font-bold text-indigo-600">
                    {filteredOpportunities[0].risk_reward_ratio}:1
                  </p>
                  <p className="text-xs text-indigo-500">
                    {filteredOpportunities[0].signal_strength} Signal
                  </p>
                </div>
              </div>
              <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-xs text-yellow-800 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  <strong>Disclaimer:</strong> This is for informational purposes only. Always do your own research before trading.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info Footer */}
        <div className="text-center text-xs text-slate-500 py-4">
          <p>Scanner analyzes 60 top NASDAQ & NYSE stocks for support/resistance levels</p>
          <p>Risk/Reward calculated using 5-day high/low • Auto-refresh: 5 minutes</p>
        </div>
      </div>
    </div>
  );
};

export default ScannerPage;
